import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { testingToolsApi } from "~/redux/warehouse/testingTools";

import { DEFAULT_TASK_GROUP_SIZE } from "./binToPort/binToPortConstants";

import {
  EventFeedFilters,
  PtlDevicePingState,
  PtlNotification,
  PtlPhaseStatus,
  PtlPingOutcome,
  TestStepStatus,
  PtlTestingSession,
  PtlTestPhase,
  EventStatus,
  TestingTool,
  SingleStepSession,
  AdditionalTestProperties,
  PTLSessionPhaseKey,
  SingleStepTestingTool,
  MultiStepSessionKey,
  SingleStepSessionKey,
  BinToPortSession,
  BinCircusErrorType
} from "./testingTools.types";
import { getTestingToolStatusFromEventStatus } from "./testingToolsUtils";

// Default ordered phases for a PTL testing session
export const defaultPtlPhases: PtlTestPhase[] = [
  "layout_verification",
  "ping_verification"
];

// Unified list of all testing tools, including PTL
export const testingToolList: TestingTool[] = [
  "Scanner",
  "Bin to Port",
  "PTL"
] as const;

export const testingToolSessionsMap: Record<
  TestingTool,
  SingleStepSessionKey | MultiStepSessionKey
> = {
  Scanner: "scannerTestSession",
  "Bin to Port": "binToPortTestSession",
  PTL: "ptlTestSession"
};

/**
 * Initialize a new PTL testing session.
 * - Uses `defaultPtlPhases` in order
 * - Sets each phase’s initial status to "preview"
 */
export const getInitialPtlSession = (): PtlTestingSession => ({
  phases: defaultPtlPhases.reduce<Record<string, PtlPhaseStatus>>(
    (acc, phase, phaseIndex) => ({
      ...acc,
      [`${phaseIndex + 1}`]: {
        phase,
        status: "preview"
      }
    }),
    {}
  ),
  activePhase: {
    phaseIndex: "1",
    notification: null
  }
});

/**
 * Initialize ping-state for each device ID
 * before running the ping phase.
 */
export const getInitialIndividualPTLStatusForPingTest = (
  ptlIdList: number[]
): Record<string, PtlDevicePingState> =>
  ptlIdList.reduce<Record<string, PtlDevicePingState>>((acc, ptlId) => {
    return {
      ...acc,
      [ptlId]: {
        requested: false,
        status: null,
        startedAt: null
      }
    };
  }, {});

// Labels for filtering the event feed
export const eventFeedFiltersList: EventFeedFilters = {
  Success: "success events",
  Failed: "error events",
  Info: "info events"
};

export type TestingToolsState = {
  events: {
    text: string;
    state: EventStatus;
    time: Date;
    testOrigin: TestingTool;
  }[];
  eventsFeedFilters: string[];
  ptlTestSession: PtlTestingSession;
  scannerTestSession: SingleStepSession;
  binToPortTestSession: BinToPortSession;
};

export const initialState: TestingToolsState = {
  events: [],
  eventsFeedFilters: [],
  ptlTestSession: getInitialPtlSession(),
  scannerTestSession: {
    lastScannedBarcode: null,
    status: "preview"
  },
  binToPortTestSession: {
    status: "preview",
    portStatusById: {},
    taskGroupSize: DEFAULT_TASK_GROUP_SIZE
  }
};

export const testingToolsSlice = createSlice({
  name: "testingTools",
  initialState,
  reducers: {
    setSingleStepTestStatus(
      state,
      {
        payload
      }: PayloadAction<{
        testingToolName: SingleStepTestingTool;
        eventStatus: EventStatus;
        skipLogging?: boolean;
        additionalProps?: AdditionalTestProperties;
      }>
    ) {
      const {
        testingToolName,
        eventStatus,
        skipLogging,
        additionalProps = {}
      } = payload;
      const { lastScannedBarcode } = additionalProps;

      const testingToolStatus =
        getTestingToolStatusFromEventStatus(eventStatus);
      const testingToolSessionKey = testingToolSessionsMap[
        testingToolName
      ] as SingleStepSessionKey;

      // only update the status if it is different from the current one
      if (state[testingToolSessionKey].status !== testingToolStatus)
        state[testingToolSessionKey].status = testingToolStatus;

      if (testingToolName === "Scanner" && lastScannedBarcode !== undefined) {
        state[testingToolSessionKey].lastScannedBarcode = lastScannedBarcode;
      }

      if (!skipLogging)
        state.events.push({
          text: `${testingToolName} test: ${eventStatus}`,
          state: eventStatus,
          time: new Date(),
          testOrigin: testingToolName
        });
    },
    setTaskGroupSize(state, { payload }: PayloadAction<number>) {
      state.binToPortTestSession.taskGroupSize = payload;
    },
    addEvent(
      state,
      {
        payload
      }: PayloadAction<{
        text: string;
        state: EventStatus;
        testOrigin: TestingTool;
      }>
    ) {
      state.events.push({ ...payload, time: new Date() });
    },
    resetTestingToolsState(state) {
      state.events = [];
      state.ptlTestSession = getInitialPtlSession();
      state.scannerTestSession = {
        lastScannedBarcode: null,
        status: "preview"
      };
      state.binToPortTestSession = {
        status: "preview",
        portStatusById: {},
        taskGroupSize: DEFAULT_TASK_GROUP_SIZE
      };
      state.eventsFeedFilters = [];
    },
    resetSingleStepSession: (
      state,
      { payload }: PayloadAction<SingleStepTestingTool>
    ) => {
      const testingToolSessionKey = testingToolSessionsMap[
        payload
      ] as SingleStepSessionKey;

      state[testingToolSessionKey].status = "preview";
      if (payload === "Scanner") {
        state[testingToolSessionKey].lastScannedBarcode = null;
      }
      if (payload === "Bin to Port") {
        state.binToPortTestSession.portStatusById = {};
        state.binToPortTestSession.taskGroupSize = DEFAULT_TASK_GROUP_SIZE;
      }
    },
    setBinCircusPortCycling: (state, action: PayloadAction<number>) => {
      const portId = action.payload;
      state.binToPortTestSession.portStatusById[portId] = {
        portState: "cycling",
        error: null,
        completedOn: null
      };
    },
    setBinCircusPortIdle: (state, action: PayloadAction<number>) => {
      const portId = action.payload;
      state.binToPortTestSession.portStatusById[portId] = {
        portState: "idle",
        error: null,
        completedOn: new Date()
      };
    },
    setBinCircusPortError: (
      state,
      action: PayloadAction<{
        portId: number;
        error: string;
        errorType: BinCircusErrorType;
      }>
    ) => {
      const { portId, error, errorType } = action.payload;
      const currentStatus = state.binToPortTestSession.portStatusById[portId];
      state.binToPortTestSession.portStatusById[portId] = {
        portState: currentStatus?.portState ?? "idle",
        error: {
          message: error,
          type: errorType,
          isRetrying: false
        },
        completedOn: currentStatus?.completedOn ?? null
      };
    },
    setBinCircusPortRetrying: (state, action: PayloadAction<number>) => {
      const portId = action.payload;
      const currentStatus = state.binToPortTestSession.portStatusById[portId];
      if (currentStatus?.error) {
        currentStatus.error.isRetrying = true;
      }
    },
    resetAllBinCircusPortStatuses: (state) => {
      state.binToPortTestSession.portStatusById = {};
    },
    setEventsFeedFilters(state, { payload }: PayloadAction<string[]>) {
      state.eventsFeedFilters = payload;
    },
    clearEventsFeedFilters(state) {
      state.eventsFeedFilters = [];
    },
    setPhaseStatus: (
      state,
      action: PayloadAction<{
        phaseIndex: PTLSessionPhaseKey;
        newState: TestStepStatus;
        ptlIdList?: number[];
      }>
    ) => {
      const { phaseIndex, newState, ptlIdList } = action.payload;
      const phaseToUpdate = state.ptlTestSession.phases[
        phaseIndex
      ] as PtlPhaseStatus;
      if (!phaseToUpdate) return;

      const isSelectedPhaseToUpdateActive =
        state.ptlTestSession.activePhase.phaseIndex === phaseIndex;

      state.ptlTestSession.phases[phaseIndex].status = newState;

      if (
        phaseToUpdate.phase === "ping_verification" &&
        !phaseToUpdate.deviceStatusById &&
        ptlIdList
      ) {
        state.ptlTestSession.phases[phaseIndex].deviceStatusById =
          getInitialIndividualPTLStatusForPingTest(ptlIdList);
      }

      if (isSelectedPhaseToUpdateActive && newState === "completed") {
        state.ptlTestSession.activePhase.notification = null;
      }
    },
    /**
     * Handles forward-only transition to a new PTL phase, optionally overriding status.
     * Marks previous phase as completed (if applicable) and clears notification.
     */
    transitionToPtlPhase: (
      state,
      action: PayloadAction<{
        phaseIndex: PTLSessionPhaseKey;
        phaseStatus?: Partial<PtlPhaseStatus>;
        ptlIdList?: number[];
      }>
    ) => {
      const {
        phaseIndex: nextPhaseIndex,
        phaseStatus,
        ptlIdList
      } = action.payload;
      const prevPhaseIndex = state.ptlTestSession.activePhase.phaseIndex;
      const prevIndexNumber = Number(prevPhaseIndex);
      const nextIndexNumber = Number(nextPhaseIndex);
      if (
        isNaN(prevIndexNumber) ||
        isNaN(nextIndexNumber) ||
        prevIndexNumber >= nextIndexNumber
      ) {
        return;
      }

      const nextPhase = state.ptlTestSession.phases[nextPhaseIndex];
      const prevPhase = state.ptlTestSession.phases[prevPhaseIndex];

      if (prevPhase?.status !== "completed") {
        prevPhase.status = "completed";
      }

      // Update the next phase's status
      const updatedNextPhase = {
        ...nextPhase,
        ...phaseStatus,
        status:
          phaseStatus?.status ||
          (nextPhase?.status === "preview" ? "running" : nextPhase?.status),
        ...(ptlIdList && {
          deviceStatusById: getInitialIndividualPTLStatusForPingTest(ptlIdList)
        })
      };
      state.ptlTestSession.activePhase = {
        phaseIndex: nextPhaseIndex,
        notification: null
      };
      state.ptlTestSession.phases[nextPhaseIndex] = updatedNextPhase;
      state.ptlTestSession.phases[prevPhaseIndex] = prevPhase;
    },
    /**
     * Flags active phase with an error/warning and displays a user-facing notification.
     */
    setPtlPhaseNotification: (
      state,
      action: PayloadAction<PtlNotification>
    ) => {
      const notification = action.payload;
      const activePhaseIndex = state.ptlTestSession.activePhase.phaseIndex;

      state.ptlTestSession.activePhase.notification = notification;

      if (state.ptlTestSession.phases[activePhaseIndex]) {
        state.ptlTestSession.phases[activePhaseIndex].status = "error";
      }
    },
    setPtlDeviceTestStatus: (
      state,
      action: PayloadAction<{
        ptlId: number;
        status: {
          pingOutcome?: PtlPingOutcome;
          requested?: boolean;
        };
      }>
    ) => {
      const {
        ptlId,
        status: { pingOutcome, requested }
      } = action.payload;
      const currentPhaseIndex = state.ptlTestSession.activePhase.phaseIndex;
      const selectedTestPhase = state.ptlTestSession.phases["2"];

      if (
        !selectedTestPhase ||
        currentPhaseIndex !== "2" ||
        selectedTestPhase.phase !== "ping_verification"
      )
        return;

      const startedAt = pingOutcome === "pending" ? new Date() : null;
      const individualPTLStatusDataForPingTest =
        selectedTestPhase.deviceStatusById;

      if (!individualPTLStatusDataForPingTest) {
        state.ptlTestSession.phases["2"].deviceStatusById = {
          [ptlId]: {
            status: pingOutcome || null,
            requested: requested || false,
            startedAt
          }
        };
      } else if (individualPTLStatusDataForPingTest[ptlId]) {
        individualPTLStatusDataForPingTest[ptlId] = {
          ...individualPTLStatusDataForPingTest[ptlId],
          ...(pingOutcome !== undefined && { status: pingOutcome }),
          ...(requested !== undefined && { requested }),
          startedAt
        };
      }
    },
    resetPtlTestSession: (state) => {
      state.ptlTestSession = getInitialPtlSession();
    }
  },
  selectors: {
    selectEventFeed: (state: TestingToolsState) => state.events,
    selectEventsFeedFilters: (state: TestingToolsState) =>
      state.eventsFeedFilters,
    selectPtlPhaseStatuses: (state: TestingToolsState) =>
      state.ptlTestSession.phases,
    selectActivePtlPhaseId: (state: TestingToolsState) =>
      state.ptlTestSession.activePhase.phaseIndex,
    selectActivePtlNotification: (state: TestingToolsState) =>
      state.ptlTestSession.activePhase.notification ?? null,
    selectScannerTestSession: (state: TestingToolsState) =>
      state.scannerTestSession,
    selectBinToPortTestSession: (state: TestingToolsState) =>
      state.binToPortTestSession,
    selectBinCircusPortStatusById: (state: TestingToolsState) =>
      state.binToPortTestSession.portStatusById,
    selectTaskGroupSize: (state: TestingToolsState) =>
      state.binToPortTestSession.taskGroupSize
  },
  extraReducers: (builder) => {
    const { startPtlTest, getPortStatusForBinCircus } =
      testingToolsApi.endpoints;

    builder.addMatcher(startPtlTest.matchFulfilled, (state, { meta }) => {
      const [ptlId] = meta.arg.originalArgs.displaysToActivate;
      if (!ptlId) return;
      state.ptlTestSession.phases["2"].deviceStatusById =
        state.ptlTestSession.phases["2"].deviceStatusById ?? {};

      state.ptlTestSession.phases["2"].deviceStatusById[ptlId] = {
        requested: true,
        status: "pending",
        startedAt: new Date()
      };
    });

    builder.addMatcher(
      getPortStatusForBinCircus.matchFulfilled,
      (state, { payload, meta }) => {
        const { portId } = meta.arg.originalArgs;
        const { binCircusRunning } = payload;
        const currentStatus = state.binToPortTestSession.portStatusById[portId];

        state.binToPortTestSession.portStatusById[portId] = {
          portState: binCircusRunning ? "cycling" : "idle",
          error: currentStatus?.error ?? null,
          // Preserve existing completedOn if set, clear if cycling
          completedOn: binCircusRunning
            ? null
            : (currentStatus?.completedOn ?? null)
        };
      }
    );
  }
});

export const {
  setSingleStepTestStatus,
  addEvent,
  resetTestingToolsState,
  setEventsFeedFilters,
  clearEventsFeedFilters,
  setPhaseStatus,
  transitionToPtlPhase,
  setPtlPhaseNotification,
  setPtlDeviceTestStatus,
  resetPtlTestSession,
  resetSingleStepSession,
  setBinCircusPortCycling,
  setBinCircusPortIdle,
  setBinCircusPortError,
  setBinCircusPortRetrying,
  resetAllBinCircusPortStatuses,
  setTaskGroupSize
} = testingToolsSlice.actions;

export const {
  selectEventFeed,
  selectEventsFeedFilters,
  selectPtlPhaseStatuses,
  selectActivePtlPhaseId,
  selectActivePtlNotification,
  selectBinToPortTestSession,
  selectScannerTestSession,
  selectBinCircusPortStatusById,
  selectTaskGroupSize
} = testingToolsSlice.selectors;

export const selectActivePtlPhaseDetails = createSelector(
  [selectActivePtlPhaseId, selectActivePtlNotification, selectPtlPhaseStatuses],
  (
    phaseIndex,
    notification,
    ptlSessionSteps
  ): PtlPhaseStatus & {
    phaseIndex: PTLSessionPhaseKey;
    notification: PtlNotification | null;
  } => {
    const activePhaseFullDetails = ptlSessionSteps[phaseIndex];
    return {
      phaseIndex,
      notification,
      ...activePhaseFullDetails
    };
  }
);

export const selectPtlDeviceStatusForActivePhase = (
  putToLightModuleId?: number
) =>
  createSelector([selectActivePtlPhaseDetails], (activePhase) => {
    if (!putToLightModuleId || !activePhase) return undefined;

    const { phase, status, deviceStatusById } = activePhase;
    const isAfterPreviewPing =
      phase === "ping_verification" && status !== "preview";

    if (isAfterPreviewPing && deviceStatusById) {
      return deviceStatusById[putToLightModuleId.toString()];
    }

    return undefined;
  });

export const testingToolsReducer = testingToolsSlice.reducer;
